import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  FormHelperText,
  Switch,
  FormControlLabel,
  Slider,
  Autocomplete,
  Chip as MuiChip,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Save,
  PlayArrow,
  Check,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { campaignsAPI, utilityAPI, sourcesAPI, suppressionListsAPI } from '../../../services/leadGenAPI';

const steps = [
  'Basic Information',
  'Lead Source Configuration',
  'Targeting & Suppression',
  'Review & Launch'
];

const CreateCampaign = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    name: '',
    description: '',
    crmCampaign: '',
    leadType: '',
    targetCount: 100,
    budgetLimit: '',
    startDate: null,
    endDate: null,

    // Step 2: Lead Source Configuration
    primarySource: '',
    backupSources: [],
    sourceConfig: {},
    costPerLead: 0,

    // Step 3: Targeting & Suppression
    targetingCriteria: {},
    suppressionLists: [],
    complianceRules: {},
    geographicFilters: {},
    demographicFilters: {},

    // Step 4: Review & Launch
    launchMode: 'draft', // 'draft' or 'active'
  });

  // Mock data for dropdowns
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [suppressionLists, setSuppressionLists] = useState([]);

  useEffect(() => {
    fetchDropdownData();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [crmCampaignsData, leadSourcesData, suppressionListsData] = await Promise.all([
        utilityAPI.getCrmCampaigns(),
        sourcesAPI.getSources(),
        suppressionListsAPI.getLists(),
      ]);

      setCrmCampaigns(crmCampaignsData.results || crmCampaignsData);
      setLeadSources(leadSourcesData.results || leadSourcesData);
      setSuppressionLists(suppressionListsData.results || suppressionListsData);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      // Set empty arrays if API calls fail
      setCrmCampaigns([]);
      setLeadSources([]);
      setSuppressionLists([]);
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0:
        if (!formData.name.trim()) newErrors.name = 'Campaign name is required';
        if (!formData.crmCampaign) newErrors.crmCampaign = 'CRM Campaign is required';
        if (!formData.leadType) newErrors.leadType = 'Lead type is required';
        if (formData.targetCount <= 0) newErrors.targetCount = 'Target count must be greater than 0';
        break;
      case 1:
        if (!formData.primarySource) newErrors.primarySource = 'Primary source is required';
        if (formData.costPerLead <= 0) newErrors.costPerLead = 'Cost per lead must be greater than 0';
        break;
      case 2:
        // Optional validation for targeting
        break;
      case 3:
        // Final validation
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSave = async () => {
    if (!validateStep(activeStep)) return;

    setLoading(true);
    try {
      // Prepare campaign data for API
      const campaignData = {
        name: formData.name,
        description: formData.description,
        crm_campaign: formData.crmCampaign,
        lead_type: formData.leadType,
        target_count: formData.targetCount,
        budget_limit: formData.budgetLimit,
        start_date: formData.startDate ? formData.startDate.toISOString() : null,
        end_date: formData.endDate ? formData.endDate.toISOString() : null,
        primary_lead_source_id: formData.primarySource,
        backup_sources: formData.backupSources,
        source_config: formData.sourceConfig,
        cost_per_lead: formData.costPerLead,
        targeting_criteria: formData.targetingCriteria,
        suppression_lists: formData.suppressionLists,
        compliance_rules: formData.complianceRules,
        geographic_filters: formData.geographicFilters,
        demographic_filters: formData.demographicFilters,
        launch_mode: formData.launchMode,
      };

      await campaignsAPI.createCampaign(campaignData);
      navigate('/lead-gen/campaigns');
    } catch (error) {
      console.error('Error saving campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="text.primary">
                  Campaign Details
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Campaign Name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.crmCampaign}>
                  <InputLabel>CRM Campaign</InputLabel>
                  <Select
                    value={formData.crmCampaign}
                    label="CRM Campaign"
                    onChange={(e) => handleInputChange('crmCampaign', e.target.value)}
                  >
                    {crmCampaigns.map((campaign) => (
                      <MenuItem key={campaign.id} value={campaign.id}>
                        {campaign.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.crmCampaign && (
                    <FormHelperText>{errors.crmCampaign}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errors.leadType}>
                  <InputLabel>Lead Type</InputLabel>
                  <Select
                    value={formData.leadType}
                    label="Lead Type"
                    onChange={(e) => handleInputChange('leadType', e.target.value)}
                  >
                    <MenuItem value="B2B">B2B</MenuItem>
                    <MenuItem value="D2C">D2C</MenuItem>
                    <MenuItem value="Mixed">Mixed</MenuItem>
                  </Select>
                  {errors.leadType && (
                    <FormHelperText>{errors.leadType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Target Lead Count"
                  type="number"
                  value={formData.targetCount}
                  onChange={(e) => handleInputChange('targetCount', parseInt(e.target.value))}
                  error={!!errors.targetCount}
                  helperText={errors.targetCount}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Budget Limit (Optional)"
                  type="number"
                  value={formData.budgetLimit}
                  onChange={(e) => handleInputChange('budgetLimit', e.target.value)}
                  placeholder="Enter budget limit"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date (Optional)"
                  value={formData.startDate}
                  onChange={(date) => handleInputChange('startDate', date)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Lead Source Configuration
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.primarySource}>
                <InputLabel>Primary Lead Source</InputLabel>
                <Select
                  value={formData.primarySource}
                  label="Primary Lead Source"
                  onChange={(e) => {
                    handleInputChange('primarySource', e.target.value);
                    const source = leadSources.find(s => s.id === e.target.value);
                    if (source) {
                      handleInputChange('costPerLead', source.costPerLead);
                    }
                  }}
                >
                  {leadSources.map((source) => (
                    <MenuItem key={source.id} value={source.id}>
                      {source.name} (${source.costPerLead}/lead)
                    </MenuItem>
                  ))}
                </Select>
                {errors.primarySource && (
                  <FormHelperText>{errors.primarySource}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost per Lead"
                type="number"
                value={formData.costPerLead}
                onChange={(e) => handleInputChange('costPerLead', parseFloat(e.target.value))}
                error={!!errors.costPerLead}
                helperText={errors.costPerLead}
                InputProps={{
                  startAdornment: '$',
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={leadSources}
                getOptionLabel={(option) => option.name}
                value={formData.backupSources}
                onChange={(event, newValue) => handleInputChange('backupSources', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Backup Lead Sources"
                    placeholder="Select backup sources"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <MuiChip
                      label={option.name}
                      {...getTagProps({ index })}
                      size="small"
                    />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Source Configuration
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Configure specific settings for your selected lead sources.
                  </Typography>
                  {/* TODO: Dynamic form based on selected sources */}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Targeting & Suppression
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Targeting Criteria
                  </Typography>
                  <Typography variant="body2" color="textSecondary" mb={2}>
                    Define who you want to target with this campaign.
                  </Typography>
                  {/* TODO: Visual rule builder */}
                  <TextField
                    fullWidth
                    label="Targeting Rules (JSON)"
                    multiline
                    rows={4}
                    placeholder='{"industry": ["technology", "finance"], "company_size": "50-500"}'
                    value={JSON.stringify(formData.targetingCriteria, null, 2)}
                    onChange={(e) => {
                      try {
                        const parsed = JSON.parse(e.target.value);
                        handleInputChange('targetingCriteria', parsed);
                      } catch (error) {
                        // Invalid JSON, keep as string
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                multiple
                options={suppressionLists}
                getOptionLabel={(option) => option.name}
                value={formData.suppressionLists}
                onChange={(event, newValue) => handleInputChange('suppressionLists', newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Suppression Lists"
                    placeholder="Select lists to exclude"
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <MuiChip
                      label={option.name}
                      {...getTagProps({ index })}
                      size="small"
                      color="warning"
                    />
                  ))
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Geographic Filters
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.geographicFilters.enabled || false}
                        onChange={(e) => handleInputChange('geographicFilters', {
                          ...formData.geographicFilters,
                          enabled: e.target.checked
                        })}
                      />
                    }
                    label="Enable geographic filtering"
                  />
                  {/* TODO: Geographic filter UI */}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Demographic Filters
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.demographicFilters.enabled || false}
                        onChange={(e) => handleInputChange('demographicFilters', {
                          ...formData.demographicFilters,
                          enabled: e.target.checked
                        })}
                      />
                    }
                    label="Enable demographic filtering"
                  />
                  {/* TODO: Demographic filter UI */}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom color="text.primary">
                Campaign Summary
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Basic Information
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Name</Typography>
                    <Typography variant="body1">{formData.name}</Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Lead Type</Typography>
                    <Chip label={formData.leadType} size="small" />
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Target Count</Typography>
                    <Typography variant="body1">{formData.targetCount} leads</Typography>
                  </Box>
                  {formData.budgetLimit && (
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Budget Limit</Typography>
                      <Typography variant="body1">${formData.budgetLimit}</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Lead Sources
                  </Typography>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Primary Source</Typography>
                    <Typography variant="body1">
                      {leadSources.find(s => s.id === formData.primarySource)?.name}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">Cost per Lead</Typography>
                    <Typography variant="body1">${formData.costPerLead}</Typography>
                  </Box>
                  {formData.backupSources.length > 0 && (
                    <Box mb={2}>
                      <Typography variant="body2" color="textSecondary">Backup Sources</Typography>
                      {formData.backupSources.map((source, index) => (
                        <Chip key={index} label={source.name} size="small" sx={{ mr: 1, mb: 1 }} />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom color="text.primary">
                    Launch Options
                  </Typography>
                  <FormControl component="fieldset">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.launchMode === 'active'}
                          onChange={(e) => handleInputChange('launchMode', e.target.checked ? 'active' : 'draft')}
                        />
                      }
                      label="Launch campaign immediately"
                    />
                  </FormControl>
                  <Typography variant="body2" color="textSecondary" mt={1}>
                    {formData.launchMode === 'active' 
                      ? 'Campaign will start generating leads immediately after creation.'
                      : 'Campaign will be saved as a draft for later activation.'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Estimated Cost:</strong> ${(formData.targetCount * formData.costPerLead).toFixed(2)}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Create Lead Generation Campaign
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/lead-gen/campaigns')}
        >
          Cancel
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {activeStep === steps.length ? (
          <Box textAlign="center">
            <Check sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom color="text.primary">
              Campaign Created Successfully!
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/lead-gen/campaigns')}
              sx={{ mt: 2 }}
            >
              View Campaigns
            </Button>
          </Box>
        ) : (
          <>
            {renderStepContent(activeStep)}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBack />}
              >
                Back
              </Button>
              <Box>
                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <PlayArrow />}
                  >
                    {formData.launchMode === 'active' ? 'Launch Campaign' : 'Save as Draft'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    endIcon={<ArrowForward />}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CreateCampaign; 